/**
 * ContentSage Suite - Landing Page
 *
 * Theme: "The Future of Content Strategy" (SaaS Vibe: Purple/Amber/Dark)
 *
 * Sections:
 * 1. Hero - "The Only AI That Plans, Writes, and Links Your Content"
 * 2. Innovation Engine - Weaver's Web, Truth Spell, PlanCraft
 * 3. Mobile Ecosystem - iOS/Android announcement
 * 4. Tool Suite - BlogCraft, PlanCraft, CopyForge with credits
 * 5. Footer
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Check,
  ChevronRight,
  ExternalLink,
  FileText,
  Globe,
  Link2,
  Mail,
  Menu,
  Rocket,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Wand2,
  X,
  Calendar,
  PenTool,
  Zap,
  Play,
  BookOpen,
  Target,
  Clock,
} from 'lucide-react';
import { MobileWelcomeScreen } from '../components/mobile';
import { Platform } from '../utils/platform';
import ContentSageLogo from '../components/ContentSageLogo';
import { MauticForm } from '../components/MauticForm';

// Navigation Items
const NAV_ITEMS = [
  { label: 'Suite', href: '#suite' },
  { label: 'Mobile', href: '#mobile' },
  { label: 'Pricing', href: '/pricing' },
];

// Hero Rotating Headlines
const HERO_HEADLINES = [
  'Writes SEO Blogs',
  'Builds Strategy',
  'Verifies Facts',
  'Links Content',
];

// Innovation Features
const INNOVATIONS = [
  {
    id: 'weavers-web',
    icon: Link2,
    title: "The Weaver's Web",
    subtitle: 'Automated Internal Linking',
    description:
      "Most AI writers leave your pages orphaned. We crawl your site history and auto-inject contextually relevant internal links to boost your domain authority instantly.",
    gradient: 'from-purple-500/20 to-indigo-500/20',
    borderColor: 'border-purple-500/30',
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/10',
  },
  {
    id: 'truth-spell',
    icon: ShieldCheck,
    title: 'The Truth Spell',
    subtitle: 'Verified Citations',
    description:
      "Zero hallucinations. Every factual claim is cross-referenced against 200M+ academic papers from PubMed & Google Scholar. If we say it, we cite it.",
    gradient: 'from-emerald-500/20 to-teal-500/20',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  {
    id: 'plancraft',
    icon: Target,
    title: 'PlanCraft',
    subtitle: 'Strategy Engine',
    description:
      "Don't just write—strategize. Our new AI Agent interviews you to build a comprehensive Business Model Canvas (BMC) and exports a professional 20-page Business Plan PDF.",
    gradient: 'from-amber-500/20 to-orange-500/20',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/10',
    isNew: true,
  },
];

// Tool Suite - Currently Available
const TOOLS = [
  {
    id: 'blogcraft',
    name: 'BlogCraft',
    description: 'SEO Blog Writer',
    icon: FileText,
    credits: '1 Credit / Post',
    gradient: 'from-purple-600 to-purple-500',
    features: ['2000+ word articles', 'AI-generated images', 'Auto internal linking', 'Citation verification'],
  },
  {
    id: 'plancraft',
    name: 'PlanCraft',
    description: 'Business Strategy PDF',
    icon: Target,
    credits: '25 Credits / Plan',
    gradient: 'from-amber-600 to-amber-500',
    features: ['Business Model Canvas', '20-page PDF export', 'AI-guided interview', 'Competitive analysis'],
  },
  {
    id: 'planner',
    name: 'Planner',
    description: 'Editorial Calendar',
    icon: Calendar,
    credits: '0 Credits',
    gradient: 'from-indigo-600 to-indigo-500',
    features: ['AI-powered content planning', 'Monthly topic clusters', 'One-click blog generation', 'Progress tracking'],
  },
];

// Coming Soon - V2 Features
const COMING_SOON_TOOLS = [
  {
    id: 'copyforge',
    name: 'CopyForge',
    description: 'Landing Page Copy',
    icon: PenTool,
    brief: 'Generate conversion-focused website copy including hero sections, feature blocks, CTAs, and A/B variations.',
  },
  {
    id: 'seobooster',
    name: 'SEO Booster',
    description: 'Content Optimization',
    icon: Globe,
    brief: 'Audit and optimize existing content for search rankings with keyword analysis and meta tag generation.',
  },
  {
    id: 'imageforge',
    name: 'ImageForge',
    description: 'AI Image Generation',
    icon: Sparkles,
    brief: 'Create stunning blog images, social graphics, and marketing visuals with AI-powered generation.',
  },
  {
    id: 'linkmaster',
    name: 'LinkMaster',
    description: 'Internal Linking',
    icon: Link2,
    brief: 'Automated internal linking analysis with orphan page detection and anchor text optimization.',
  },
];

// Mobile Features
const MOBILE_FEATURES = [
  {
    icon: Sparkles,
    title: 'Draft on the Go',
    description: 'Start a blog post voice note on your phone.',
  },
  {
    icon: BookOpen,
    title: 'Review Strategy',
    description: 'Access your PlanCraft PDFs in meetings.',
  },
  {
    icon: Calendar,
    title: 'Track Progress',
    description: 'Check your Editorial Calendar status anywhere.',
  },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(Platform.isNative() || (Platform.isMobileBrowser() && isSmallScreen));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Rotate headlines
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeadlineIndex((prev) => (prev + 1) % HERO_HEADLINES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Show clean mobile welcome screen on native/mobile
  if (isMobile) {
    return <MobileWelcomeScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950">
      {/* ==================== NAVIGATION ==================== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <ContentSageLogo />

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-10">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-slate-400 hover:text-white transition font-medium"
                >
                  {item.label}
                </a>
              ))}
            </div>

            {/* Desktop CTA - Login Button with Gradient */}
            <div className="hidden md:flex items-center gap-4">
              <Link
                to="https://write-gts.cchk.info/login"
                className="bg-gradient-to-r from-purple-600 to-amber-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:opacity-90 transition shadow-lg shadow-purple-500/25"
              >
                Login
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              <Link
                to="https://write-gts.cchk.info/login"
                className="bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold px-4 py-2 rounded-lg"
              >
                Login
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-slate-800 transition"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-white" />
                ) : (
                  <Menu className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-slate-900 border-t border-slate-800"
            >
              <div className="px-4 py-4 space-y-1">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition"
                  >
                    {item.label}
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </a>
                ))}
                <div className="pt-3 border-t border-slate-800">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-purple-600 to-amber-500 text-white px-4 py-3 rounded-xl font-semibold hover:opacity-90 transition"
                  >
                    Launch Web App
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ==================== HERO SECTION ==================== */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <span className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-300 px-4 py-2 rounded-full text-sm font-medium mb-8 border border-purple-500/20">
              <Sparkles className="w-4 h-4" />
              The Future of Content Strategy
            </span>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight">
              The Only AI That{' '}
              <span className="block mt-2">
                <span className="bg-gradient-to-r from-purple-400 via-amber-400 to-purple-400 bg-clip-text text-transparent">
                  Plans, Writes, and Links
                </span>
              </span>
              <span className="block mt-2">Your Content.</span>
            </h1>

            {/* Animated Subheadline */}
            <div className="h-16 md:h-20 flex items-center justify-center mb-8">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentHeadlineIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-xl md:text-2xl text-slate-400"
                >
                  The AI that{' '}
                  <span className="text-white font-semibold">
                    {HERO_HEADLINES[currentHeadlineIndex]}
                  </span>
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Subheadline Static */}
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
              Stop using disjointed tools. ContentSage automates your entire workflow—from
              Business Strategy (BMC) to SEO-optimized publishing with verified citations.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-amber-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition shadow-lg shadow-purple-500/25"
              >
                <Rocket className="w-5 h-5" />
                Launch Web App
              </Link>
              <a
                href="#suite"
                className="inline-flex items-center justify-center gap-2 bg-slate-800/50 text-slate-300 px-8 py-4 rounded-xl font-semibold text-lg border border-slate-700 hover:border-purple-500/50 hover:text-white transition backdrop-blur-sm"
              >
                <Play className="w-5 h-5" />
                See How It Works
              </a>
            </div>

            {/* Coming Soon Badge */}
            <p className="text-sm text-slate-500">
              <Smartphone className="w-4 h-4 inline mr-1" />
              Also coming soon to iOS & Android
            </p>
          </motion.div>
        </div>
      </section>

      {/* ==================== INNOVATION ENGINE ==================== */}
      <section id="suite" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-amber-500/20">
              <Zap className="w-4 h-4" />
              Why We Are Different
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              The Innovation Engine
            </h2>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
              Three breakthrough technologies that set ContentSage apart from every other AI writer.
            </p>
          </motion.div>

          {/* Innovation Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {INNOVATIONS.map((innovation, index) => (
              <motion.div
                key={innovation.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-gradient-to-br ${innovation.gradient} rounded-2xl p-8 border ${innovation.borderColor} backdrop-blur-sm hover:border-opacity-60 transition group`}
              >
                {/* New Badge */}
                {innovation.isNew && (
                  <span className="absolute top-4 right-4 bg-amber-500 text-slate-900 text-xs font-bold px-2 py-1 rounded-full">
                    NEW
                  </span>
                )}

                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl ${innovation.iconBg} flex items-center justify-center mb-6 group-hover:scale-110 transition border ${innovation.borderColor}`}>
                  <innovation.icon className={`w-7 h-7 ${innovation.iconColor}`} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-2">{innovation.title}</h3>
                <p className={`text-sm ${innovation.iconColor} italic mb-4`}>{innovation.subtitle}</p>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {innovation.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== MOBILE ECOSYSTEM ==================== */}
      <section id="mobile" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-purple-500/20">
                <Smartphone className="w-4 h-4" />
                Coming Soon
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                Your Content Studio,{' '}
                <span className="bg-gradient-to-r from-purple-400 to-amber-400 bg-clip-text text-transparent">
                  Now in Your Pocket.
                </span>
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Sync your strategy across Web, Tablet, and Mobile. Create content anywhere, anytime.
              </p>

              {/* Features List */}
              <div className="space-y-6 mb-8">
                {MOBILE_FEATURES.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-purple-500/20">
                      <feature.icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{feature.title}</h4>
                      <p className="text-slate-400 text-sm">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* App Store Badges */}
              <div className="flex flex-wrap gap-4">
                <button className="flex items-center gap-3 bg-slate-800 hover:bg-slate-700 transition px-5 py-3 rounded-xl border border-slate-700">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs text-slate-400">Coming to</p>
                    <p className="text-white font-semibold">App Store</p>
                  </div>
                </button>
                <button className="flex items-center gap-3 bg-slate-800 hover:bg-slate-700 transition px-5 py-3 rounded-xl border border-slate-700">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-xs text-slate-400">Coming to</p>
                    <p className="text-white font-semibold">Google Play</p>
                  </div>
                </button>
              </div>
            </motion.div>

            {/* Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="relative flex justify-center"
            >
              <div className="relative">
                {/* Phone Frame */}
                <div className="w-[280px] h-[580px] bg-slate-800 rounded-[3rem] p-3 border-4 border-slate-700 shadow-2xl">
                  {/* Screen */}
                  <div className="w-full h-full bg-gradient-to-br from-slate-900 to-purple-950 rounded-[2.5rem] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-slate-800 rounded-b-2xl" />

                    {/* App Content Preview */}
                    <div className="pt-12 px-4">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-amber-500 rounded-lg flex items-center justify-center">
                            <Wand2 className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-white font-semibold text-sm">ContentSage</span>
                        </div>
                        <div className="w-8 h-8 bg-slate-800 rounded-full" />
                      </div>

                      {/* Apps Grid Preview */}
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        {[
                          { icon: FileText, label: 'BlogCraft', color: 'from-purple-600 to-purple-500' },
                          { icon: Target, label: 'PlanCraft', color: 'from-amber-600 to-amber-500' },
                          { icon: Calendar, label: 'Calendar', color: 'from-indigo-600 to-indigo-500' },
                        ].map((app) => (
                          <div key={app.label} className="flex flex-col items-center gap-1">
                            <div className={`w-14 h-14 bg-gradient-to-br ${app.color} rounded-2xl flex items-center justify-center`}>
                              <app.icon className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-slate-400 text-[10px]">{app.label}</span>
                          </div>
                        ))}
                      </div>

                      {/* Recent Activity */}
                      <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50">
                        <p className="text-slate-400 text-[10px] mb-2">RECENT</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-2">
                            <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                              <FileText className="w-4 h-4 text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs truncate">AI in Healthcare</p>
                              <p className="text-slate-500 text-[10px]">Draft • 2h ago</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-purple-500/20 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-amber-500/20 rounded-full blur-xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== TOOL SUITE ==================== */}
      <section id="pricing" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-purple-500/20">
              <Sparkles className="w-4 h-4" />
              Universal Credit Model
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              One Subscription.{' '}
              <span className="bg-gradient-to-r from-purple-400 to-amber-400 bg-clip-text text-transparent">
                Unlimited Power.
              </span>
            </h2>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
              Access the entire suite with a single credit balance. Use what you need, when you need it.
            </p>
          </motion.div>

          {/* Tools Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {TOOLS.map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-800/30 rounded-2xl border border-slate-700/50 overflow-hidden backdrop-blur-sm hover:border-purple-500/30 transition group"
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${tool.gradient} p-6`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <tool.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{tool.name}</h3>
                      <p className="text-white/70 text-sm">{tool.description}</p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  {/* Credit Cost */}
                  <div className="mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-400 font-semibold">{tool.credits}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3">
                    {tool.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-slate-300 text-sm">
                        <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Coming Soon - V2 Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 bg-slate-800/50 text-slate-400 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-slate-700/50">
                <Rocket className="w-4 h-4" />
                Coming in V2
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                More Power on the Horizon
              </h3>
              <p className="text-slate-400 max-w-xl mx-auto">
                We're building even more tools to supercharge your content workflow.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {COMING_SOON_TOOLS.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-slate-800/20 rounded-xl p-5 border border-slate-700/30 backdrop-blur-sm relative overflow-hidden group hover:border-slate-600/50 transition"
                >
                  {/* Coming Soon Badge */}
                  <span className="absolute top-3 right-3 text-[10px] font-bold bg-slate-700/50 text-slate-400 px-2 py-0.5 rounded-full">
                    SOON
                  </span>

                  {/* Icon */}
                  <div className="w-10 h-10 bg-slate-700/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-700/50 transition">
                    <tool.icon className="w-5 h-5 text-slate-400" />
                  </div>

                  {/* Content */}
                  <h4 className="text-white font-semibold mb-1">{tool.name}</h4>
                  <p className="text-slate-500 text-xs mb-3">{tool.description}</p>
                  <p className="text-slate-400 text-sm leading-relaxed">{tool.brief}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-amber-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition shadow-lg shadow-purple-500/25"
            >
              <Rocket className="w-5 h-5" />
              Start Creating Today
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="mt-4 text-slate-500 text-sm">
              No credit card required for trial
            </p>
          </motion.div>

          {/* Demo Request Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-16 max-w-xl mx-auto"
          >
            <div className="bg-slate-800/30 rounded-2xl border border-slate-700/50 p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-2 text-center">Request a Demo</h3>
              <p className="text-slate-400 text-sm mb-6 text-center">
                See how ContentSage can transform your content strategy
              </p>
              <MauticForm formId={10} className="mautic-demo-request" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Company Info & Newsletter */}
            <div className="md:col-span-2">
              <ContentSageLogo className="mb-6" />
              <p className="text-slate-400 max-w-md mb-6">
                The complete AI content suite that plans your strategy, writes SEO-optimized blogs,
                and builds internal linking—all with verified citations.
              </p>

              {/* Newsletter Signup */}
              <div className="mb-6">
                <h4 className="font-semibold text-white mb-3">Stay Updated</h4>
                <p className="text-slate-400 text-sm mb-4">Get the latest AI content tips and updates.</p>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <MauticForm formId={9} className="mautic-newsletter" />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <a href="#" aria-label="ContentSage website" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition">
                  <Globe className="w-5 h-5" />
                </a>
                <a href="mailto:hello@contentsage.ai" aria-label="Email ContentSage" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Suite */}
            <div>
              <h4 className="font-semibold text-white mb-4">Suite</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="#suite" className="text-slate-400 hover:text-white transition">
                    BlogCraft
                  </a>
                </li>
                <li>
                  <a href="#suite" className="text-slate-400 hover:text-white transition">
                    PlanCraft
                  </a>
                </li>
                <li>
                  <a href="#suite" className="text-slate-400 hover:text-white transition">
                    Planner
                  </a>
                </li>
                <li>
                  <a href="#suite" className="text-slate-400 hover:text-white transition flex items-center gap-1">
                    CopyForge
                    <span className="text-xs bg-slate-700/50 text-slate-400 px-2 py-0.5 rounded-full">V2</span>
                  </a>
                </li>
                <li>
                  <a href="#mobile" className="text-slate-400 hover:text-white transition flex items-center gap-1">
                    Mobile Apps
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">Soon</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#pricing" className="text-slate-400 hover:text-white transition">Pricing</a></li>
                <li>
                  <Link to="https://write-gts.cchk.info/login" className="text-slate-400 hover:text-white transition flex items-center gap-1">
                    Login
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </li>
                <li><a href="#" className="text-slate-400 hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="pt-8 border-t border-slate-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-slate-400">
                © {new Date().getFullYear()} ContentSage. A Ganda Tech Services Product.
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <span>Powered by</span>
                <span className="text-purple-400 font-medium">Claude AI</span>
                <span>&</span>
                <span className="text-amber-400 font-medium">Gemini</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-slate-950 via-slate-950/95 to-transparent pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <Link
          to="/dashboard"
          className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-purple-600 to-amber-500 text-white px-6 py-4 rounded-xl font-semibold text-base shadow-lg shadow-purple-500/25"
        >
          <Rocket className="w-5 h-5" />
          Launch Web App
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Extra padding for mobile sticky CTA */}
      <div className="md:hidden h-24" />
    </div>
  );
}
